import { useEffect, useState } from "react";
import { getCompliances } from "@/api/compliances";
import useMutateData from "@/hooks/useMutateData";
import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Loader2 } from "lucide-react";
import {patchCandidateCompliance} from "@/api/candidateCompliances.jsx";

export default function CandidateCompliances({ candidateId, compliances = [], updateCandidateData }) {
    const [complianceTypes, setComplianceTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { mutate } = useMutateData();
    const [modalOpen, setModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(null);
    const [currentCompliance, setCurrentCompliance] = useState(null);
    const [file, setFile] = useState(null);

    useEffect(() => {
        const fetchCompliances = async () => {
            try {
                const data = await getCompliances();
                if (Array.isArray(data)) setComplianceTypes(data);
            } catch (err) {
                setError("Failed to load compliances");
            } finally {
                setLoading(false);
            }
        };
        fetchCompliances();
    }, []);

    const openModal = (compliance) => {
        console.log(compliance)
        setCurrentCompliance(compliance);
        setUploadSuccess(null);
        setModalOpen(true);
    };

    const openViewModal = (compliance) => {
        setCurrentCompliance(compliance);
        setViewModalOpen(true);
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append("document", file);
        formData.append("candidate_id", candidateId);

        try {
            const result = await patchCandidateCompliance(candidateId, currentCompliance.compliance?.id, formData)
            setUploading(false);
            console.log(result)
            if (result?.id) {
                const updatedCompliance = result;
                updateCandidateData((prevData) => {
                    const existingIndex = prevData.candidateCompliances.findIndex(
                        (c) => c.compliance.id === updatedCompliance.compliance.id
                    );
                    const updatedCompliances = [...prevData.candidateCompliances];
                    if (existingIndex !== -1) {
                        updatedCompliances[existingIndex] = updatedCompliance;
                    } else {
                        updatedCompliances.push(updatedCompliance);
                    }
                    return {
                        ...prevData,
                        candidateCompliances: updatedCompliances
                    };
                });
                setUploadSuccess(true);
            } else {
                setUploadSuccess(false);
            }
        } catch (error) {
            setUploading(false);
            setUploadSuccess(false);
        }
    };

    const handleDownload = (url, name) => {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name || "compliance_document");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return <p className="p-6">Loading compliances...</p>;
    if (error) return <p className="p-6 text-red-600">{error}</p>;

    return (
        <div className="p-4 border rounded-lg bg-white shadow-sm">
            <h3 className="text-xl font-bold mb-4">Compliances</h3>
            <ul className="space-y-3">
                {complianceTypes.map((type) => {
                    const compliance = compliances.find((c) => c.compliance.id === type.id);
                    const isImage = compliance?.contentType?.startsWith("image/");

                    return (
                        <li key={type.id} className="flex justify-between items-center p-3 border rounded-lg bg-gray-50 hover:bg-gray-100">
                            <span className="font-medium">{type.name}</span>
                            <div className="space-x-2">
                                {compliance && compliance.documentUrl ? (
                                    <>
                                        {isImage && (
                                            <Button variant="outline" onClick={() => openViewModal(compliance)}>View</Button>
                                        )}
                                        <Button variant="default" onClick={() => handleDownload(compliance.documentUrl, compliance.name)}>Download</Button>
                                        <Button onClick={() => openModal(compliance)}>Edit</Button>
                                    </>
                                ) : (
                                    <Button onClick={() => openModal({ complianceId: type.id, compliance: { name: type.name, id:type.id } })}>Add</Button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>


            {/* Edit/Upload Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{currentCompliance?.documentUrl ? "Edit Compliance" : "Add Compliance"}</DialogTitle>
                    </DialogHeader>

                    {/* ✅ Wrap inside a form with `enctype="multipart/form-data"` */}
                    {/* ✅ Wrap inside a form with `enctype="multipart/form-data"` */}
                    <form onSubmit={handleUpload} encType="multipart/form-data">
                        {uploading ? (
                            <div className="flex flex-col items-center justify-center p-6">
                                <Loader2 className="animate-spin h-8 w-8 text-[#5D1F42]" />
                                <p className="mt-2 text-gray-700">Uploading...</p>
                            </div>
                        ) : uploadSuccess !== null ? (
                            <div className="flex flex-col items-center justify-center p-6">
                                {uploadSuccess ? (
                                    <p className="text-green-600 font-medium">Upload Successful!</p>
                                ) : (
                                    <p className="text-red-600 font-medium">Upload Failed. Please try again.</p>
                                )}
                                <Button className="mt-4 bg-[#5D1F42] text-white hover:bg-[#4a1834]" onClick={() => setModalOpen(false)}>
                                    Close
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-4 py-2">
                                    <p className="text-sm text-gray-700">
                                        Uploading compliance for: <strong>{currentCompliance?.compliance?.name}</strong>
                                    </p>

                                    <label className="block text-sm font-medium text-[#5D1F42]">Select a file:</label>
                                    <Input
                                        type="file"
                                        accept="image/*,application/pdf"
                                        onChange={handleFileChange}
                                        className="border-[#5D1F42] focus:ring-[#5D1F42]"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                                    <Button
                                        type="submit"
                                        disabled={!file}
                                        className={`bg-[#5D1F42] text-white hover:bg-[#4a1834] ${!file ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        Upload
                                    </Button>
                                </DialogFooter>
                            </>
                        )}
                    </form>

                </DialogContent>
            </Dialog>

            {/* Image View Modal */}
            <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>View Compliance</DialogTitle>
                    </DialogHeader>
                    {currentCompliance?.documentUrl ? (
                        <img
                            src={currentCompliance.documentUrl}
                            alt="Compliance Document"
                            className="max-w-full max-h-[70vh] mx-auto"
                        />
                    ) : (
                        <p className="text-red-600">No image available</p>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setViewModalOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
